import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './MercadoPagoPaymentModal.css';

/**
 * MercadoPagoPaymentModal - Modal para pagos con MercadoPago Checkout Pro
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - checkoutUrl: string - init_point de la preferencia MP
 * - amount: number
 * - status: null | 'pending' | 'approved' | 'rejected' - resultado del pago (tras retorno MP)
 * - onContinue: function - callback tras ver el status
 */
function MercadoPagoPaymentModal({ isOpen, onClose, checkoutUrl, amount, status, onContinue }) {
  const { t } = useTranslation();

  // Auto-redirect si tenemos la URL y no hay status (primer apertura)
  useEffect(() => {
    if (isOpen && checkoutUrl && !status) {
      window.location.href = checkoutUrl;
    }
  }, [isOpen, checkoutUrl, status]);

  if (!isOpen) return null;

  const handleGoToMP = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const statusConfig = {
    approved: {
      icon: 'check_circle',
      className: 'mp-status-approved',
      title: t('payment.mp.approved'),
      message: t('payment.mp.approvedMessage'),
    },
    pending: {
      icon: 'schedule',
      className: 'mp-status-pending',
      title: t('payment.mp.pending'),
      message: t('payment.mp.pendingMessage'),
    },
    rejected: {
      icon: 'cancel',
      className: 'mp-status-rejected',
      title: t('payment.mp.rejected'),
      message: t('payment.mp.rejectedMessage'),
    },
  };

  const currentStatus = status && statusConfig[status];

  return (
    <div className="mp-modal-overlay" onClick={onClose}>
      <div className="mp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mp-modal-header">
          <div className="mp-modal-brand">
            <span className="material-icons mp-brand-icon" aria-hidden="true">qr_code_2</span>
            <span>{t('payment.mp.title')}</span>
          </div>
          <button className="mp-close-btn" onClick={onClose}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        </div>

        <div className="mp-modal-body">
          {!status && checkoutUrl && (
            <div className="mp-redirect-state">
              <div className="mp-spinner"></div>
              <p>{t('payment.mp.redirecting')}</p>
              <p className="mp-redirect-note">{t('payment.mp.redirectNote')}</p>
              <button className="mp-checkout-btn" onClick={handleGoToMP}>
                <span className="material-icons" aria-hidden="true">open_in_new</span>
                {t('payment.mp.openCheckout')}
              </button>
            </div>
          )}

          {currentStatus && (
            <div className={`mp-status-state ${currentStatus.className}`}>
              <span className="material-icons mp-status-icon" aria-hidden="true">{currentStatus.icon}</span>
              <h3>{currentStatus.title}</h3>
              <p>{currentStatus.message}</p>
            </div>
          )}
        </div>

        <div className="mp-modal-footer">
          {amount && (
            <div className="mp-amount">
              <span>{t('payment.total')}:</span>
              <strong>${amount.toFixed(2)}</strong>
            </div>
          )}
          <p className="mp-secure-note">
            <span className="material-icons" aria-hidden="true">lock</span>
            {t('payment.mp.secure')}
          </p>
          {status && (
            <button className="mp-continue-btn" onClick={onContinue || onClose}>
              {t('common.continue')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MercadoPagoPaymentModal;
